import React from 'react';
import { Button, IconButton, Box } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import UnitPageShell from '../../components/shared/UnitPageShell';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import ZoomableImage from '../../components/shared/ZoomableImage';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { useUnitOverride, uploadUnitAsset, updateUnitContent } from '../../services/unitOverridesService';

const PAGE_ID = 'm3_u2_x2_verbraucher';

const images = [
  { key: 'placeholder', src: '/assets/drawable/wind_ice.jpeg', alt: 'Kälteverbraucher Placeholder', label: 'Placeholder' }
];

const textBlocks = [
  {
    id: 'block_intro',
    heading: 'Kälteverbraucher und die Auswirkungen auf den Kältekreis',
    body:
      'Kälteverbraucher entziehen einem Raum oder Prozess Wärme und führen diese über ein Kältemedium (meist Kaltwasser) an einen Kälteerzeuger ab. ' +
      'Die Auswahl hängt von den Anforderungen an Komfort, Effizienz, Regelbarkeit und den baulichen Gegebenheiten ab. ' +
      'Jeder Kälteverbraucher stellt spezifische Anforderungen an den Kältekreis, insbesondere an die Kaltwassertemperaturen, die Regelung und die notwendigen Komponenten zur Vermeidung von Kondensation.'
  },
  {
    id: 'block_kuehldecken_beschreibung',
    heading: 'Kühldecken (Flächenkühlung) – Beschreibung',
    body:
      'Kühldecken sind in die Deckenkonstruktion integrierte Rohrsysteme (ähnlich Fußbodenheizung, aber für Kühlung). Kaltes Wasser durchströmt die Rohre und kühlt die Deckenfläche. ' +
      'Die Wärmeabfuhr erfolgt primär durch Strahlung und zu einem geringeren Teil durch Konvektion. Sie bieten hohen Komfort durch zugfreie Kühlung.\n\n' +
      'Vorteile: Hoher Strahlungsanteil, zugfrei, geräuschlos, gleichmäßige Temperaturverteilung, kann oft auch zum Heizen verwendet werden (Klimadecken).\n' +
      'Nachteile: Begrenzte Kühlleistung pro m² (Taupunkt!), träger als Konvektionssysteme, Installation kann aufwendig sein.'
  },
  {
    id: 'block_kuehldecken_regelung',
    heading: 'Kühldecken – Kältekreis & Regelung',
    body:
      'Kühldecken benötigen relativ hohe Kaltwassertemperaturen, um Kondensation an der Deckenoberfläche zu vermeiden. Typische Vorlauftemperaturen liegen bei 16-18°C (minimal 1-2 K über dem Raumlufttaupunkt).\n\n' +
      'Regelstrategien:\n' +
      '• Raumtemperaturregelung über Raumthermostate, die Ventile im Kaltwasserkreis steuern.\n' +
      '• Zwingend: Taupunktüberwachung! Ein Taupunktsensor im Raum oder Referenzraum misst die Feuchtigkeit und Temperatur. Droht Kondensation, muss die Kaltwassertemperatur angehoben oder der Kühlkreis temporär abgeschaltet/gedrosselt werden.\n' +
      '• Einzelraumregelung ist Standard.\n\n' +
      'Sicherheitskomponenten:\n' +
      '• Taupunktsensoren und entsprechende Regelalgorithmen.\n' +
      '• Leckageerkennungssysteme in sensiblen Bereichen.'
  },
  {
    id: 'block_kuehlsegel_beschreibung',
    heading: 'Kühlsegel – Beschreibung',
    body:
      'Kühlsegel sind abgehängte Elemente, die von Kaltwasser durchströmt werden. Sie funktionieren ähnlich wie Kühldecken, bieten aber mehr Flexibilität in der Anordnung und können auch akustisch wirksam sein. Wärmeabfuhr erfolgt durch Strahlung und Konvektion.\n\n' +
      'Vorteile: Gute Kühlleistung, Flexibilität, oft mit Akustik- oder Lichtfunktionen kombinierbar, schnellere Reaktion als massive Kühldecken.\n' +
      'Nachteile: Sichtbar, erfordern Platz unter der Decke, Taupunktüberwachung ebenfalls kritisch.'
  },
  {
    id: 'block_kuehlsegel_regelung',
    heading: 'Kühlsegel – Kältekreis & Regelung',
    body:
      'Ähnlich wie Kühldecken, Kaltwassertemperaturen von 16-18°C. Die Kühlleistung kann durch die freie Umströmung etwas höher sein als bei geschlossenen Kühldecken.\n\n' +
      'Regelstrategien:\n' +
      '• Einzelraumregelung mit Raumthermostaten und Ventilen.\n' +
      '• Taupunktüberwachung ist essentiell.\n\n' +
      'Sicherheitskomponenten:\n' +
      '• Taupunktsensoren.'
  },
  {
    id: 'block_fancoil_beschreibung',
    heading: 'Gebläsekonvektoren (Fan Coils) – Beschreibung',
    body:
      'Gebläsekonvektoren (auch Fan-Coil-Units genannt) bestehen aus einem Wärmeübertrager (Kühlregister) und einem Ventilator. Raumluft wird angesaugt, über das Kaltwasserregister geführt, gekühlt (und oft entfeuchtet) und wieder in den Raum geblasen. Es gibt sie als Decken-, Wand-, Stand- oder Unterflurgeräte.\n\n' +
      'Vorteile: Hohe Kühlleistung, schnelle Raumkühlung, können auch heizen (4-Leiter-Systeme) und lüften/filtern.\n' +
      'Nachteile: Mögliche Geräuschentwicklung durch Ventilator, Zugerscheinungen bei falscher Auslegung/Platzierung, Kondensat muss abgeführt werden.'
  },
  {
    id: 'block_fancoil_regelung',
    heading: 'Gebläsekonvektoren – Kältekreis & Regelung',
    body:
      'Fan Coils arbeiten mit niedrigeren Kaltwassertemperaturen (z.B. 6/12°C Vor-/Rücklauf oder 7/12°C) für eine effektive Kühlung und Entfeuchtung.\n\n' +
      'Regelstrategien:\n' +
      '• Raumtemperaturregelung über Thermostat, der den Ventilator (oft mehrstufig oder stufenlos) und/oder ein Regelventil im Kaltwasserzulauf steuert.\n' +
      '• Kondensatpumpe oder freier Ablauf für anfallendes Kondensat erforderlich und muss überwacht werden (Überlaufschutz).\n\n' +
      'Sicherheitskomponenten:\n' +
      '• Kondensatwannenüberwachung (Schwimmerschalter) zur Vermeidung von Wasserschäden.\n' +
      '• Frostschutz für das Register bei Außenluftansaugung im Winter (falls zum Heizen genutzt).'
  },
  {
    id: 'block_luftkuehler_beschreibung',
    heading: 'Luftkühler in RLT-Anlagen (Kühlregister) – Beschreibung',
    body:
      'In zentralen Raumlufttechnischen (RLT) Anlagen werden große Luftvolumenströme über wasserdurchströmte Kühlregister (Lamellenwärmetauscher) gekühlt und entfeuchtet, bevor die Luft in die Räume verteilt wird.\n\n' +
      'Vorteile: Zentrale und effiziente Kühlung und Entfeuchtung großer Luftmengen, Möglichkeit zur Wärmerückgewinnung und Integration weiterer Luftbehandlungsfunktionen (Filtration, Befeuchtung).\n' +
      'Nachteile: Hoher Planungsaufwand, Platzbedarf für Kanalsystem und RLT-Gerät, Kosten.'
  },
  {
    id: 'block_luftkuehler_regelung',
    heading: 'Luftkühler in RLT-Anlagen – Kältekreis & Regelung',
    body:
      'Kühlregister in RLT-Anlagen verwenden ebenfalls niedrige Kaltwassertemperaturen (z.B. 6/12°C).\n\n' +
      'Regelstrategien:\n' +
      '• Regelung der Zulufttemperatur oder Raumtemperatur über ein Stetigventil (z.B. 0-10V) am Kaltwasserzulauf zum Register.\n' +
      '• Oft kombiniert mit variabler Volumenstromregelung der Luft.\n' +
      '• Kondensat muss gesammelt und abgeführt werden.\n\n' +
      'Sicherheitskomponenten:\n' +
      '• Kondensatwannenüberwachung.\n' +
      '• Frostschutzthermostat/-wächter, um Einfrieren des Registers bei kalten Außentemperaturen und abgeschaltetem oder geringem Luftvolumenstrom zu verhindern (öffnet Ventil, stoppt Ventilator, schließt Außenluftklappe).'
  },
  {
    id: 'block_bkt_beschreibung',
    heading: 'Betonkerntemperierung (BKT) zum Kühlen – Beschreibung',
    body:
      'Ähnlich der BKT zum Heizen werden Rohre in massive Betondecken oder -wände eingelegt. Durchströmt mit Kaltwasser, kühlen sie die Bauteilmasse und somit indirekt den Raum. Primär Strahlungsaustausch.\n\n' +
      'Vorteile: Sehr energieeffizient, geräuschlos, nutzt Speichermasse zur Lastverschiebung (z.B. Nachtauskühlung).\n' +
      'Nachteile: Extrem träge, nur zur Grundlastabdeckung geeignet, Taupunktüberwachung kritisch.'
  },
  {
    id: 'block_bkt_regelung',
    heading: 'BKT – Kältekreis & Regelung',
    body:
      'Kaltwassertemperaturen sind relativ hoch (z.B. 17-20°C), um Kondensation sicher zu vermeiden. Die Spreizung ist gering (2-4 K).\n\n' +
      'Regelstrategien:\n' +
      '• Oft konstante Vorlauftemperatur oder sehr flache, witterungsgeführte Kühlkurve.\n' +
      '• Prädiktive Regelung (Wettervorhersage, Gebäudenutzung) ist ideal.\n' +
      '• Zwingend: Taupunktüberwachung mit Anhebung der Kaltwassertemperatur oder Abschaltung bei Bedarf.\n\n' +
      'Sicherheitskomponenten:\n' +
      '• Taupunktsensoren und entsprechende Regelung.'
  },
  {
    id: 'block_kassetten_beschreibung',
    heading: 'Kaltwasser-Kassetten – Beschreibung',
    body:
      'Kaltwasser-Kassetten sind eine spezielle Bauform von Gebläsekonvektoren, die typischerweise in abgehängte Decken (Rasterdecken) integriert werden. Sie saugen Raumluft mittig an, kühlen diese über ein Kaltwasserregister und blasen die gekühlte Luft oft 4-seitig wieder aus.\n\n' +
      'Vorteile: Unauffällige Integration in Decken, gute Luftverteilung, relativ hohe Kühlleistung.\n' +
      'Nachteile: Wie bei Fan Coils: Kondensatabfuhr, mögliche Geräusche, Wartung in der Decke.'
  },
  {
    id: 'block_kassetten_regelung',
    heading: 'Kaltwasser-Kassetten – Kältekreis & Regelung',
    body:
      'Identisch zu anderen Gebläsekonvektoren, Kaltwassertemperaturen z.B. 6/12°C.\n\n' +
      'Regelstrategien:\n' +
      '• Integrierte oder externe Raumtemperaturregelung, Steuerung von Ventilatorstufen und Kaltwasserventil.\n' +
      '• Kondensatpumpe ist meist integriert.\n\n' +
      'Sicherheitskomponenten:\n' +
      '• Kondensatüberwachung (Schwimmerschalter).'
  }
];

const M3_U2_X2_Verbraucher: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM3, canUploadImagesM3 } = useAuth();
  const canEditTexts = canEditTextsM3;
  const canUploadImages = canUploadImagesM3;
  const { override, refresh } = useUnitOverride(PAGE_ID);

  const imgSrc = (key: string, fallback: string) => {
    const v = override?.images?.[key];
    if (!v) return fallback;
    const s = String(v);
    return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`;
  };

  const mergedBlocks = React.useMemo(() => 
    textBlocks.map(tb => ({
      ...tb,
      body: override?.texts?.[tb.id] || tb.body
    })),
    [override]
  );

  const fullText = React.useMemo(() => mergedBlocks.map(b => `${b.heading}. ${b.body}`).join(' '), [mergedBlocks]);
  const { speak, stop, speaking } = useTTS({ text: fullText });

  const handleTextSave = React.useCallback(async (blockId: string, body: string) => {
    if (!token) return alert('Kein Token.');
    if (!canEditTexts) return alert('Keine Berechtigung zum Texte bearbeiten.');
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockId]: body } }, token);
      alert('Text gespeichert.');
      await refresh();
    } catch (e: any) {
      alert('Speichern fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh, canEditTexts]);

  const handleImageReplace = React.useCallback(async (key: string, file: File) => {
    if (!token) return alert('Kein Token.');
    if (!canUploadImages) return alert('Keine Berechtigung fuer Bilder-Uploads.');
    try {
      await uploadUnitAsset(PAGE_ID, key, file, token);
      alert('Bild aktualisiert.');
      await refresh();
    } catch (e: any) {
      alert('Upload fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh, canUploadImages]);

  const ttsBtn = React.createElement(IconButton, { onClick: speaking ? stop : speak, 'aria-label': 'Vorlesen', sx: { ml: 1 } }, React.createElement(SpeakerIcon, { sx: { fontSize: 28 } }));

  return React.createElement(UnitPageShell, { title: '3.2.2 Kälteverbraucher', actions: ttsBtn },
    React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' }, gap: 2 } },
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        images.map(img => React.createElement(ZoomableImage, {
          key: img.key,
          src: imgSrc(img.key, img.src),
          alt: img.alt,
          label: 'Bild einfügen',
          onReplace: canUploadImages ? (file: File) => handleImageReplace(img.key, file) : undefined
        }))
      ),
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        mergedBlocks.map(tb => React.createElement(EditableTextBlock, {
          key: tb.id,
          pageId: PAGE_ID,
          blockId: tb.id,
          heading: tb.heading,
          body: tb.body,
          onSave: (body: string) => handleTextSave(tb.id, body)
        })),
        React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } },
          React.createElement(Button, { variant: 'outlined', onClick: () => navigate('/units/m3_u2_x99_quiz') }, 'Zum Quiz')
        )
      )
    ),
    React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } })
  );
};

export default M3_U2_X2_Verbraucher;

